import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { gsap } from "gsap";
import React, { useCallback, useEffect, useRef } from "react";
import {
  getSuggestedWidgetKey,
  selectBannerAlerts,
  useStore,
} from "../../store";
// import { Alert } from "../../types";
import { AlertBannerItem } from "./AlertBannerItem";
import { useShallow } from "zustand/shallow";
import { widgetsCatalog, WidgetKey } from "../../data/widgets";
import { keyboardService } from "../../services/keyboardService";
// import { walkthroughService } from "../../services/walkthroughService";

const POSITION_STYLES: Record<
  1 | 2 | 3 | 4 | "OVER",
  {
    x: number;
    y: number;
    scale: number;
    zIndex: number;
    filter: string;
  }
> = {
  1: {
    x: 0,
    y: 0,
    scale: 1,
    zIndex: 20 as number,
    filter: "brightness(1.0)",
  },
  2: {
    x: 1,
    y: 8,
    scale: 0.98,
    zIndex: 15 as number,
    filter: "brightness(0.75)",
  },
  3: {
    x: 2,
    y: 16,
    scale: 0.96,
    zIndex: 10 as number,
    filter: "brightness(0.5)",
  },
  4: {
    x: 3,
    y: 24,
    scale: 0.94,
    zIndex: 0 as number,
    filter: "brightness(0.25)",
  },
  OVER: {
    x: 0,
    y: -100,
    scale: 1,
    zIndex: 20 as number,
    filter: "brightness(1.0)",
  },
};

export const AlertBanner: React.FC = () => {
  const {
    currentAlertIndex,
    setCurrentAlertIndex,
    startWidgetSuggestion,
    // setSuggestedWidgetKey,
    markAlertInteracted,
    // assignSuggestedToIndex,
  } = useStore();
  const bannerAlerts = useStore(useShallow(selectBannerAlerts));
  const suggestedWidgetKey = useStore(useShallow(getSuggestedWidgetKey));
  const containerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  const totalAlerts = bannerAlerts.length;
  const currentAlert = bannerAlerts[currentAlertIndex];
  const { isSuggestingWidget } = useStore();

  const getAbsolutePosition = useCallback(
    (idx: number, base: number): 1 | 2 | 3 | 4 => {
      if (totalAlerts === 0) return 1;
      const n = totalAlerts;
      // 1..n (1 is front)
      const pos = ((idx - base + n) % n) + 1;
      // We only render up to 4 positions; anything beyond maps to 4
      return Math.min(pos, 4) as 1 | 2 | 3 | 4;
    },
    [totalAlerts]
  );

  // Helper to reset card positions relative to a given index
  const resetPositions = useCallback(
    (indexBase: number) => {
      if (!containerRef.current || totalAlerts === 0) return;
      bannerAlerts.forEach((alert, idx) => {
        const cardEl = cardsRef.current[idx];
        if (!cardEl) return;
        const absPos = getAbsolutePosition(idx, indexBase);
        const style = POSITION_STYLES[absPos];
        gsap.set(cardEl, style);
      });
    },
    [bannerAlerts, totalAlerts, getAbsolutePosition]
  );

  // Initialize card positions
  useEffect(() => {
    // Avoid resetting while an animation timeline is active to prevent fighting with GSAP
    if (timelineRef.current) return;
    resetPositions(currentAlertIndex);
  }, [currentAlertIndex, resetPositions]);

  // Prepare GSAP timeline and clear existing tweens
  const prepareTimeline = useCallback(() => {
    if (timelineRef.current) {
      timelineRef.current.kill();
      timelineRef.current = null;
    }
    bannerAlerts.forEach((_, index) => {
      const cardEl = cardsRef.current[index];
      if (cardEl) gsap.killTweensOf(cardEl);
    });
    const tl = gsap.timeline({
      paused: true,
      onComplete: () => {
        timelineRef.current = null;
      },
    });
    timelineRef.current = tl;
    return tl;
  }, [bannerAlerts]);

  // Rotate stack by delta (+1 next, -1 previous) using absolute positions
  const rotate = useCallback(
    (delta: 1 | -1) => {
      if (totalAlerts <= 1) return;

      const tl = prepareTimeline();

      // Read latest index to avoid stale closures
      const latestIndex = useStore.getState().currentAlertIndex;
      const prevIndex = latestIndex;
      const newIndex = (latestIndex + delta + totalAlerts) % totalAlerts;

      // Update state immediately so rapid changes accumulate
      setCurrentAlertIndex(newIndex);

      // Normalize all cards to the previous baseline
      resetPositions(prevIndex);

      // Animate all cards to their absolute positions for the new base
      const duration = 0.25;
      bannerAlerts.forEach((_, index) => {
        const cardEl = cardsRef.current[index];
        if (!cardEl) return;
        const pos = getAbsolutePosition(index, newIndex);

        // Optional fly-over accent: for next, send backmost over; for prev, send frontmost over
        const flyOver = delta === 1 ? pos === totalAlerts : pos === 1;
        if (flyOver) {
          tl.to(
            cardEl,
            {
              ...POSITION_STYLES.OVER,
              duration,
              ease: "power2.inOut",
            },
            0
          );
        }

        tl.to(
          cardEl,
          {
            ...POSITION_STYLES[pos],
            duration,
            ease: "power2.inOut",
          },
          flyOver ? undefined : 0
        );
      });

      tl.play(0);
    },
    [
      totalAlerts,
      bannerAlerts,
      setCurrentAlertIndex,
      resetPositions,
      getAbsolutePosition,
      prepareTimeline,
    ]
  );

  const animateNext = useCallback(() => rotate(1), [rotate]);

  const animatePrevious = useCallback(() => rotate(-1), [rotate]);

  // Register bracket key navigation for banner alerts
  useEffect(() => {
    const unsubscribe = keyboardService.onRawKey((code) => {
      // Do not interfere with widget suggestion flow
      const state = useStore.getState();
      if (state.isSuggestingWidget) return;
      if (code === "BracketLeft") {
        animatePrevious();
      } else if (code === "BracketRight") {
        animateNext();
      }
    });
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [animateNext, animatePrevious]);

  if (!currentAlert) {
    return (
      <div className="mx-8 w-full">
        <div className="bg-crane-dark border border-crane-neutral-border rounded-lg p-4 min-h-24 flex items-center justify-center">
          <span className="text-crane-gray">No active alerts</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-8 w-full">
      <div className="flex items-center space-x-4 justify-between h-full w-full">
        {/* Navigation */}
        <div
          className={`flex flex-col items-center space-y-1 ${
            totalAlerts > 1 ? "visible" : "invisible"
          }`}
        >
          <button
            onClick={animatePrevious}
            disabled={totalAlerts <= 1}
            className="hover:bg-crane-darker rounded p-1 transition-colors disabled:opacity-50"
          >
            <ChevronUpIcon className="w-4 h-4 text-crane-gray hover:text-crane-white" />
          </button>
          <div className="text-sm text-crane-gray font-medium">
            {currentAlertIndex + 1}/{totalAlerts}
          </div>
          <button
            onClick={animateNext}
            disabled={totalAlerts <= 1}
            className="hover:bg-crane-darker rounded p-1 transition-colors disabled:opacity-50"
          >
            <ChevronDownIcon className="w-4 h-4 text-crane-gray hover:text-crane-white" />
          </button>
        </div>

        {/* Stacked Alert Cards */}
        <div ref={containerRef} className="flex-1 relative min-h-24">
          {/* Render all alerts for GSAP animations */}
          {bannerAlerts.map((alert, i) => (
            <div
              id={`alert-banner-item-${i}`}
              ref={(el) => {
                cardsRef.current[i] = el;
              }}
              className="absolute inset-0"
              // style={{ zIndex: 0, filter: "brightness(0.25)" }}
            >
              <AlertBannerItem alert={alert} />
            </div>
          ))}
          {isSuggestingWidget && suggestedWidgetKey && (
            // <div className="absolute left-1/2 top-full -translate-x-1/2 mt-2 z-30">
            //   <div className="bg-black/80 text-white text-sm px-4 py-2 rounded-lg shadow-lg border border-black/40 whitespace-nowrap">
            //     Scroll with [ and ] to pick a tile, press Enter to add {""}
            //     {widgetsCatalog[suggestedWidgetKey as WidgetKey]?.label} info.
            //   </div>
            // </div>
            <div
              className="bg-[#212121] rounded-lg p-2 absolute left-1/2 top-full -translate-x-1/2 mt-6 z-30 shadow-2xl flex cursor-pointer items-center gap-4"
              // onClick={() => startWidgetSuggestion(suggestedWidgetKey)}
              // className="crane-button-secondary py-0 pl-0 pr-3 inline-flex items-center "
              title={`Add ${widgetsCatalog[suggestedWidgetKey].label} info`}
            >
              <span className="w-11 h-11 rounded-md flex items-center justify-center">
                <img
                  className=""
                  src={widgetsCatalog[suggestedWidgetKey].icon}
                  alt={widgetsCatalog[suggestedWidgetKey].label}
                />
              </span>
              <span className="py-[10px] whitespace-nowrap">
                Select a tile to replace with{" "}
                {widgetsCatalog[suggestedWidgetKey as WidgetKey]?.label} info.
                Use mouse or [ and ] to navigate. Enter to confirm.
              </span>
            </div>
          )}
          {!isSuggestingWidget && suggestedWidgetKey && (
            <div
              className="bg-crane-dark rounded-lg p-4 absolute left-1/2 top-full -translate-x-1/2 mt-2 z-30 shadow-sm flex cursor-pointer items-center gap-4"
              onClick={() => {
                try {
                  const curr = currentAlert;
                  if (curr) markAlertInteracted(curr.id);
                } catch {}
                startWidgetSuggestion(suggestedWidgetKey);
                // assignSuggestedToIndex(2, suggestedWidgetKey);
              }}
              // className="crane-button-secondary py-0 pl-0 pr-3 inline-flex items-center "
              title={`Add ${widgetsCatalog[suggestedWidgetKey].label} info`}
            >
              <span className="w-11 h-11 rounded-md bg-crane-amber-dark-icon border border-crane-amber-dark flex items-center justify-center">
                <img
                  className=""
                  src={widgetsCatalog[suggestedWidgetKey].icon}
                  alt={widgetsCatalog[suggestedWidgetKey].label}
                />
              </span>
              <span className="border border-crane-light-border rounded-lg px-3 py-[10px]">{`Add ${widgetsCatalog[suggestedWidgetKey].label} info`}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
